import { css } from '@emotion/css';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { sendEvent } from '../../lib/google-analytics';
import { DreamHistory } from '../../models/dream/DreamHistory';
import { DreamHistoryItem } from './DreamHistoryItem';
import { IslandIcon } from './IslandIcon';

interface DreamHistoryListProps {
  items: DreamHistory[];
}

const Empty: React.FC = () => {
  const { t } = useTranslation('dream');
  return (
    <div
      className={css`
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        margin: 0 20px;
        padding: 28px 12px;
        position: relative;
        &::before,
        &::after {
          content: '';
          position: absolute;
          right: 0;
          left: 0;
        }
        &::before {
          border-top: 2px dashed #dcd8bf;
          top: 0;
        }
        &::after {
          border-bottom: 2px dashed #dcd8bf;
          bottom: 0;
        }
        .image {
          display: flex;
          justify-content: center;
          align-items: center;
          color: #938666;
          margin-right: 8px;
        }
      `}
    >
      <div className="image">
        <IslandIcon size={24} />
      </div>
      {t('empty_dream_history')}
    </div>
  );
};

export const DreamHistoryList: React.FC<DreamHistoryListProps> = React.memo(({ items }) => {
  const history = useHistory();
  const { t } = useTranslation('dream');

  const handleDreamHistoryItemClick = useCallback(
    (item: DreamHistory) => {
      const { address } = item.dreamLand;
      sendEvent('dream_history_detail', 'click');
      history.push(`/players/dream/history/detail/${address}`);
    },
    [history]
  );

  return (
    <section
      className={css`
        width: 100%;
        max-width: 480px;
        color: #4d3906;
        margin: 0 auto;
        padding: 32px 0 40px;
        h3 {
          font-size: 14px;
          margin: 0 20px;
        }
        ul {
          margin: 8px 0 0;
          padding: 0;
        }
        @media (max-width: 369px) {
          h3 {
            margin: 0 16px;
          }
        }
      `}
    >
      <h3>{t('top')}</h3>
      <ul>
        {(() => {
          if (items.length === 0) {
            return <Empty />;
          } else {
            return (
              <>
                {items.map(item => (
                  <DreamHistoryItem
                    key={item.dreamLand.address}
                    item={item}
                    onClick={handleDreamHistoryItemClick}
                  />
                ))}
              </>
            );
          }
        })()}
      </ul>
    </section>
  );
});
